/**
 * @fileoverview Modal de conta bloqueada (status bloqueado/cancelado). Trava o uso
 * do app e exibe o link de pagamento (Pagar.me) para regularização. Não é
 * dispensável. Auto-fecha quando o billing volta a liberar (evento socket
 * company-{id}-billing → refetch → blocked=false). Aplica-se à empresa toda
 * (NÃO restrito ao admin).
 */

import React, { useState } from "react";
import { Dialog, makeStyles } from "@material-ui/core";
import { ShieldAlert, ExternalLink, CreditCard } from "lucide-react";

import { i18n } from "../../../translate/i18n";
import { createModalDesign } from "../../../styles/modalDesign";
import { useOnboarding } from "../../../context/Onboarding/OnboardingContext";
import ChangeCardModal from "../ChangeCardModal";

const useStyles = makeStyles(theme => {
  const md = createModalDesign(theme);
  const dark = theme.palette.type === "dark";
  return {
    dialog: {
      ...md.mdDialog,
      "& .MuiDialog-paper": {
        ...md.mdDialog["& .MuiDialog-paper"],
        width: "min(460px, calc(100vw - 32px))",
        textAlign: "center"
      },
      // Mesmo backdrop do paywall: fundo escuro quase opaco (ilegível), SEM
      // backdrop-filter (o blur causava lag). Custo ~zero.
      "& .MuiBackdrop-root": {
        backgroundColor: "rgba(8, 10, 20, 0.92)"
      }
    },
    content: { padding: theme.spacing(4, 3.5, 3.5), display: "flex", flexDirection: "column", alignItems: "center", gap: theme.spacing(2) },
    iconWrap: {
      width: 64, height: 64, borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center",
      background: "rgba(239,68,68,0.12)", color: "#ef4444"
    },
    title: { fontSize: "1.25rem", fontWeight: 700, color: dark ? "#f8fafc" : "#111827" },
    desc: { fontSize: "0.9rem", lineHeight: 1.5, color: dark ? "rgba(248,250,252,0.7)" : "#6b7280" },
    payBtn: {
      ...md.mdBtnPrimary,
      height: 44,
      width: "100%",
      fontSize: "0.95rem",
      marginTop: theme.spacing(1),
      textDecoration: "none"
    },
    noLink: { fontSize: "0.82rem", color: dark ? "rgba(248,250,252,0.55)" : "#9ca3af" }
  };
});

const BlockedModal = ({ isAdmin = false }) => {
  const classes = useStyles();
  const { billing, refetchBilling } = useOnboarding();
  const [cardOpen, setCardOpen] = useState(false);

  if (!billing) return null;

  const blocked = billing.blocked; // bloqueado/cancelado (qualquer usuário)
  // Conta PAUSADA pós-trial: o admin resolve no PlanCheckoutModal (paga lá).
  // Aqui só tratamos o NÃO-admin pausado — ele não tem como pagar, então
  // travamos o app e orientamos a falar com o administrador da conta.
  const pausedNonAdmin = billing.paused && !isAdmin;
  if (!blocked && !pausedNonAdmin) return null;

  const isCancelled = billing.status === "cancelado";
  const title = isCancelled
    ? i18n.t("onboarding.blocked.titleCancelled")
    : i18n.t("onboarding.blocked.title");
  const desc = pausedNonAdmin
    ? i18n.t("onboarding.blocked.pausedAdminContact")
    : isCancelled
    ? i18n.t("onboarding.blocked.descCancelled")
    : i18n.t("onboarding.blocked.desc");
  // Link de pagamento só no fluxo bloqueado (vendedor/link manual ou PIX). Não-admin
  // pausado não paga — a orientação já está na descrição.
  const showPayLink = blocked && billing.payment_link;
  // (L5/M14) Bloqueio por inadimplência de CARTÃO (assinatura recorrente) não tem
  // payment_link. O admin regulariza trocando o cartão aqui mesmo (o modal trava a
  // navegação até a aba Faturamento) → na próxima tentativa de cobrança desbloqueia.
  const showUpdateCard = blocked && isAdmin && !!billing.subscription_id && !showPayLink;

  return (
    <Dialog open disableEscapeKeyDown className={classes.dialog} maxWidth={false}>
      <div className={classes.content}>
        <div className={classes.iconWrap}>
          <ShieldAlert size={32} />
        </div>
        <div className={classes.title}>{title}</div>
        <div className={classes.desc}>{desc}</div>
        {showPayLink ? (
          <a className={classes.payBtn} href={billing.payment_link} target="_blank" rel="noopener noreferrer">
            <ExternalLink size={18} />
            {i18n.t("onboarding.blocked.payCta")}
          </a>
        ) : showUpdateCard ? (
          <button type="button" className={classes.payBtn} onClick={() => setCardOpen(true)}>
            <CreditCard size={18} />
            {i18n.t("onboarding.blocked.updateCard")}
          </button>
        ) : (
          !pausedNonAdmin && <div className={classes.noLink}>{i18n.t("onboarding.blocked.contact")}</div>
        )}
      </div>
      {showUpdateCard && (
        <ChangeCardModal
          open={cardOpen}
          onClose={() => setCardOpen(false)}
          onChanged={() => { setCardOpen(false); refetchBilling && refetchBilling({ fresh: true }); }}
        />
      )}
    </Dialog>
  );
};

export default BlockedModal;
